<template>
  <div>
    <div class="mx-3 mt-6">
      <Card class="">
        <div class="text-jet font-normal leading-5 text-sm flex flex-col gap-2 bg-color px-2 py-2 mb-5 rounded-xl">
          <p>
            {{ 
            `Arrange ${isJobLevel ? `grade` : `step`} in order from the lowest level to the highest level within the organization structure. 
              This order will determine the progression hierarchy.`
            }}
          </p>
        </div>
        <div class="flex mb-6 gap-2" style="justify-content: flex-end">
          <Button class="text-dynamicBackBtn bg-white border border-dynamicBackBtn" @click="handleManageHierarchy">
            Manage Hierarchy
          </Button>
          <Button
            class="text-white"
            @click="handleClick"
            :background="appearance.buttonColor || defaultBTNColor"
          >
            {{ isJobLevel ? 'New Job Grade' : 'New Step' }}
          </Button>
        </div>
        <template v-if="!showError" v-slot:footer>
          <card-footer printcard reloadcard @print="print" @reload="reload()"
          @searchResult="handleSearch($event)" />
        </template>
      </Card>
    </div>
    <div v-if="!showError" class="p-3">
      <sTable
        :headers="gradeHeader"
        :items="items"
        :loading="loadingData"
        aria-label="leveltable"
        id="printMe"
        style="width: 100%"
        class="w-full"
        has-number
        :pagination-list="metaData" 
        @page="handlePage($event)" 
        @itemsPerPage="handleItemsPerPage($event)" 
        page-sync
      >
        <template v-slot:item="{ item }">
          <span v-if="item.name" class="py-5">{{ item.data.name }}</span>
          <span v-if="item.description" class="py-5">{{
            item.data.description
          }}</span>
          <span v-else-if="item.id">
            <Menu left top="-150" margin="110" class="my-2 p-0">
              <template v-slot:title>
                <icon icon-name="more_icon" size="xs" />
              </template>
              <div style="width: 116px; height: 81px" class="py-3 px-2">
                <div
                  class="flex py-1 px-3 more h-8 cursor-pointer"
                  @click="handleEdit(item.data)"
                >
                  <icon
                    icon-name="edit"
                    class-name="text-blueCrayola mr-2"
                    size="xsm"
                  />
                  <p class="pt-1">Edit</p>
                </div>
                <div
                  class="flex px-3 py-1 more h-8 cursor-pointer"
                  @click="confirmDelete(item.data.id)"
                >
                  <icon
                    icon-name="delete"
                    class-name="text-desire mr-2"
                    size="xsm"
                  />
                  <p class="pt-1">Delete</p>
                </div>
              </div>
            </Menu>
          </span>
        </template>
      </sTable>
    </div>
    <div v-else>
      <error-page />
    </div>

    <validation-observer
      ref="observer"
      rules="required"
      v-slot="{ handleSubmit }"
    >
      <RightSideBar
        v-if="openModal"
        @submit="handleSubmit(createJobGrade)"
        @close="closeModal"
        submit="Save"
        :loading="loading"
        :button-class="`bg-dynamicBackBtn text-white`"
        :disabled-button="disableBtn"
      >
        <template v-slot:title>
          <p class="text-darkPurple">
            {{ editMode ? `Edit ${levelName}` : `Add New ${levelName}` }}
          </p>
        </template>
        <template v-slot:subtitle>
          <p class="font-semibold text-darkPurple">
            {{ `Which of these best describe the ${levelName}?` }}
          </p>
        </template>
        <div>
          <div v-for="(option, index) in currentGradeStep" :key="index" class="grid grid-cols-12 gap-8 text-darkPurple"
            style="margin-top: 1.5rem"
          >
            <div class="col-span-12">
              <div class="text-sm flex gap-2">
                <p class="text-base w-auto">{{ levelName }}</p>
                <Icon
                  v-tooltip.top-start="`Max limit of 3 characters`"
                  icon-name="icon-info"
                  size="xms"
                />
              </div>
              <c-text
                placeholder="--Enter Job Grade--"
                variant="w-full h-10"
                style="height: 40px !important"
                v-model="option.name"
                :rules="['required']"
              />
            </div>

            <div class="col-span-12 mt-3">
              <div class="text-sm flex gap-2">
                <p class="text-base w-auto">
                  {{ `${levelName} Description` }}
                </p>
              </div>
              <c-textarea v-model="option.description" :rules="['required']" />
            </div>
          </div>
        </div>

        <div class="cursor-pointer mt-4" v-if="!editMode"
          @click="addAnotherJobGrade()"
        >
          <p class="text-base text-flame font-bold">Add Another</p>
        </div>
      </RightSideBar>
    </validation-observer>

    <RightSideBar v-if="isManageHierarchy" @submit="submitHierarchy" 
      @close="isManageHierarchy = false, hierarchyArray = [...items]" submit="Save" :loading="isloadingHierarchy" 
      :button-class="`bg-dynamicBackBtn text-white`"
    >
      <template v-slot:title>
        <p class="text-darkPurple">
          Manage Hierarchy
        </p>
      </template>
      <div class="w-full">
        <p class="font-semibold text-darkPurple text-base">
          {{ `Drag and drop ${isJobLevel ? `grades` : `steps`} to arrange them in the correct organizational hierarchy, 
          starting from the lowest level to the highest level.` }}
        </p>
        <div class="w-full mt-5 flex flex-col gap-5 p-3" v-if="hierarchyArray.length > 0">
          <div v-for="(item, index) in hierarchyArray" :key="item.id || index" 
            class="w-full transition-all duration-200"
            draggable="true"
            @dragstart="startDrag(index)"
            @dragover.prevent
            @drop="dropItem(index)"
          >
            <div
              class="flex gap-3 justify-start items-center text-base cursor-pointer p-3 rounded-md"
              :style="draggedIndex === index
                ? { backgroundColor: '#FFF5F2' } : {}"
            >
              <icon v-if="draggedIndex !== index" icon-name="non-active-move-icon" size="xsm" />
              <icon v-if="draggedIndex === index" icon-name="active-move-icon" size="xsm" />
              <p>{{ item.name ? item.name : '----' }}</p>
            </div>
          </div>
        </div>
      </div>
    </RightSideBar>

    <Modal v-if="dialogDel">
      <Card class="p-5 flex flex-col max-w-md">
        <div class>
          <p class="font-bold ml-4 my-5 text-center">
            {{ `Are you sure you want to delete this ${levelName}?` }}
          </p>
          <div class="flex">
            <Button
              class="text-white"
              @click="deleteJobGrade"
              :background="appearance.buttonColor || defaultBTNColor"
            >
              Yes
            </Button>
            <Button
              background-color="none"
              class="text-dynamicBackBtn ml-4 border border-solid border-dynamicBackBtn"
              @click="dialogDel = false"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </Modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { ValidationObserver } from "vee-validate";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import CardFooter from "@/components/CardFooter";
import Card from "@/components/Card";
import ErrorPage from "@/modules/Admin/error403";
import Button from "@/components/Button";
import RightSideBar from "@/components/RightSideBar";
import Menu from "@/components/Menu";
import Modal from "@/components/Modal";

export default {
  name: "JobGrade",
  components: {
    Card,
    Modal,
    Menu,
    ErrorPage,
    CardFooter,
    RightSideBar,
    CTextarea,
    ValidationObserver,
    CText,
    Button,
    Card,
    STable,
  },
  props:{
    isJobLevel:{
      type: Boolean,
      default: false,
    }
  },
  computed: {
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),
    gradeHeader(){
      return [
        { title: `${this.isJobLevel ? 'Grades' : 'Steps'}`, value: "name" },
        { title: `${this.isJobLevel ? 'Job Grade Description' : 'step Description'}`, value: "description" },
        { title: "", value: "id", image: true },
      ]
    },
    currentGradeStep() {
      return this.editMode ? [this.payload] : this.gradeStep;
    }
  },
  data() {
    return {
      isloadingHierarchy: false,
      draggedIndex: null,
      isManageHierarchy: false,
      hierarchyArray: [],
      editMode: false,
      levelName: "",
      showError: false,
      loadingData: true,
      metaData: {},
      itemsPerPage: null,
      numberOfPage: null,
      searchWord: null,
      dialogDel: false,
      loading: false,
      disableBtn: false,
      openModal: false,
      items: [],
      payload: {
        name: "",
        description: "",
      },
      gradeStep: [
        {
          name: "",
          description: "",
        },
      ],
      gradeId: null,
    };
  },

  methods: {
    async submitHierarchy(){
      this.isloadingHierarchy = true;
      const rankUpdates = this.hierarchyArray.map((item, index) => ({
        id: item.id, rank: this.hierarchyArray.length - index
      }));
      const payload = {
          orgId: this.$orgId,
          rankUpdates
      }
      try {
        const { data } = await this.$_UpdateStepGradeRanking(payload);
        this.isloadingHierarchy = false;
        this.isManageHierarchy = false;
        this.getJobGrades("");
        return this.$toasted.success(data.message, {
          duration: 5000,
        });
      } catch (error) {
        this.isloadingHierarchy = false;
        return this.$toasted.error(error, {
          duration: 5000,
        });
      }
    },
    async handleManageHierarchy() {
      try {
        if (this.isJobLevel) {
          await this.$handlePrivilege("jobLevel", "manageHierarchy");
        } else {
          await this.$handlePrivilege("gradeLevel", "manageHierarchy");
        }
        this.isManageHierarchy = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    startDrag(index) {
      this.draggedIndex = index
    },
    dropItem(dropIndex) {
      const from = this.draggedIndex
      const to = dropIndex
      if (from === null || from === to) return
      // SWAP logic
      const temp = this.hierarchyArray[from]
      this.$set(this.hierarchyArray, from, this.hierarchyArray[to])
      this.$set(this.hierarchyArray, to, temp)
      this.draggedIndex = null
    },
    closeModal(){
      this.openModal = false; 
      this.editMode = false;
      this.gradeStep = [
        {
          name: "",
          description: "",
        },
      ]
    },
    validateGradeStep() {
      const grades = this.editMode ? [this.payload] : this.gradeStep;
      const hasInvalid = grades.some(
        (grade) => grade.name && grade.name.length > 3
      );
      if (hasInvalid) {
        this.$toasted.error(
          "Maximum of 3 characters needed for Job Grade",
          { duration: 5000 }
        );
        return false;
      }
      return true;
    },
    createJobGrade() {
      if (!this.validateGradeStep()) {
        return
      } else {
        if (this.gradeId) {
          this.$_editJobGrade(this.gradeId, this.payload)
            .then((response) => {
              this.$toasted.success(response.data.message, { duration: 5000 });
              this.openModal = false;
              this.editMode = false;
              this.gradeId = null;
              this.payload.name = "";
              this.payload.description = "";
              this.getJobGrades("");
            })
            .catch((error) => {
              this.$toasted.error(error.response.data.message, {
                duration: 5000,
              });
            });
        } else {
          this.$_createJobGrade(this.gradeStep)
            .then((response) => {
              this.$toasted.success(response.data.message, { duration: 5000 });
              this.openModal = false;
              this.gradeStep = [
                {
                  name: "",
                  description: "",
                },
              ],
              this.getJobGrades("");
            })
            .catch((error) => {
              this.$toasted.error(error.response.data.message, {
                duration: 5000,
              });
            });
        }
      }
    },

    async handleClick() {
      try {
        if (this.isJobLevel) {
          await this.$handlePrivilege("jobLevel", "addJobGrade");
        } else {
          await this.$handlePrivilege("gradeLevel", "addStep");
        }
        this.openModal = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async confirmDelete(id) {
      try {
        if (this.isJobLevel) {
          await this.$handlePrivilege("jobLevel", "deleteJobGrade");
        } else {
          await this.$handlePrivilege("gradeLevel", "deleteStep");
        }
        this.gradeId = id;
        this.dialogDel = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },

    async handleEdit(data) {
      try {
        if (this.isJobLevel) {
          await this.$handlePrivilege("jobLevel", "editJobGrade");
        } else {
          await this.$handlePrivilege("gradeLevel", "editStep");
        }
        this.payload.name = data.name;
        this.payload.description = data.description;
        this.gradeId = data.id;
        this.editMode = true;
        this.openModal = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },

    deleteJobGrade() {
      this.$_deleteJobGrade(this.gradeId)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          this.dialogDel = false;
          this.gradeId = null;
          this.getJobGrades("");
        })
        .catch((error) => {
          this.$toasted.error(error.response.data.message, {
            duration: 5000,
          });
        });
    },
    async print() {
      await this.$htmlToPaper("printMe");
    },
    reload() {
      this.getJobGrades("");
    },
    handleSearch(searchWord) {
      this.loadingData = true;
      if (searchWord) {
        this.searchWord = searchWord;
        this.queryUpdate();
      } else {
        this.searchWord = null;
        this.queryUpdate();
      }
    },
    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },
    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.queryUpdate();
    },
    queryUpdate() {
      const search = this.searchWord ? `&search=${this.searchWord}` : "";
      const pageNumber = this.numberOfPage
        ? `&page=${this.numberOfPage}`
        : `&page=${this.metaData.page}`;
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : "";
      this.getJobGrades(`?${search}${pageNumber}${itemPage}`);
    },
    getJobGrades(param) {
      this.loadingData = true;
      this.$_getJobGrade(param).then((response) => {
        this.items = response.data.grades;
        this.hierarchyArray = [...this.items].sort((a, b) => a.rank - b.rank);
        this.metaData = response.data.meta;
        this.loadingData = false;
      });
    },
    addAnotherJobGrade(){
      this.gradeStep.push({
        name: "",
        description: "",
      });
    }
  },

  async mounted() {
    this.levelName = this.isJobLevel ? 'Job Grade' : 'Step';
    try {
      if (this.isJobLevel) {
        await this.$handlePrivilege("jobLevel", "viewJobGradeRegister");
      } else {
        await this.$handlePrivilege("gradeLevel", "viewStepRegister");
      }
      this.showError = false;
      this.getJobGrades("");
    } catch (error) {
      this.showError = true;
      this.loadingData = false;
    }
  },
};
</script>
<style lang="scss" scoped>
.bg-color{
  border: 1px solid #E993235C;
  background: #E9932314;
}
</style>